/**
 * NOTE: All custom environment variables must have the prefix `REACT_APP_`.
 * See https://create-react-app.dev/docs/adding-custom-environment-variables/.
 *
 */
export interface Env {
  /* API gateway origin */
  readonly REACT_APP_API_ORIGIN: string;
  readonly REACT_APP_CHECKOUT_GRAPHQL_API_ORIGIN?: string;

  /* Cognito */
  readonly REACT_APP_GANDALF_COGNITO_CLIENT_ID: string;
  readonly REACT_APP_GANDALF_COGNITO_USER_POOL_ID: string;
  readonly REACT_APP_COGNITO_GANDALF_IDP_NAME: string;
  readonly REACT_APP_COGNITO_AMAZON_FEDERATE_IDP_NAME: string;
  readonly REACT_APP_COGNITO_AWS_SKILL_BUILDER_NAME: string;
  readonly REACT_APP_GANDALF_COGNITO_APP_WEB_DOMAIN: string;
  readonly REACT_APP_GANDALF_LOGIN_WEB_DOMAIN: string;
  readonly REACT_APP_GANDALF_WEB_DOMAIN: string;
  readonly REACT_APP_GANDALF_REGION: string;

  /* For displaying a banner message across the top of the app */
  readonly REACT_APP_WARNING_BANNER_MESSAGE?: string;

  /**
   * Name of the challenge resources bucket for this stage.
   */
  readonly REACT_APP_CHALLENGE_RESOURCES_BUCKET_NAME: string;
  /**
   * Public docs S3 bucket name
   */
  readonly REACT_APP_PUBLIC_DOCS_BUCKET_NAME: string;

  /**
   * Name of the stage in which this react app is deployed.
   *
   * @example dev, staging, prod
   */
  readonly REACT_APP_STAGE_NAME: string;

  /**
   * Whether the challenge review automation feature is enabled
   */
  readonly REACT_APP_IS_CHALLENGE_REVIEW_ENABLED: string;

  /**
   * Whether we should be capturing and including LRS registration_id
   */
  readonly REACT_APP_LRS_INTEGRATION_ENABLED: string;

  /**
   * Whether we should show GenAi related UI elements
   */
  readonly REACT_APP_GEN_AI_INTEGRATION_ENABLED: string;

  /*
   * checkout url
   */
  readonly REACT_APP_CHECKOUT_URL: string;
  /*
   * billing purchase history url
   */
  readonly REACT_APP_BILLING_PURCHASE_HISTORY_URL?: string;
  /*
   * rum guestRoleArn
   */
  readonly REACT_APP_RUM_GUEST_ROLE_ARN?: string;
  /*
   * rum identity pool id
   */
  readonly REACT_APP_RUM_IDENTITY_POOL_ID?: string;
  /*
   * rum application id
   */
  readonly REACT_APP_RUM_APPLICATION_ID?: string;
  /*
   * rum application id
   */
  readonly REACT_APP_RUM_APPLICATION_REGION?: string;

  /* WAF */
  readonly REACT_APP_WAF_SCRIPT_URL: string;
  readonly REACT_APP_WAF_API_KEY: string;

  /**
   * Enables ACI events feature
   */
  readonly REACT_APP_ENABLE_ACI_EVENTS?: 'true' | 'false';

  /*
   * Classroom agreement id
   */
  readonly REACT_APP_CLASSROOM_AGREEMENT_ID?: string;

  readonly REACT_APP_ENABLE_VIBE_ID_USER?: 'true' | 'false';
  /**
   * Enables GenAI Event creation workflow
   */
  readonly REACT_APP_ENABLE_GENAI_EVENT_CREATE?: 'true' | 'false';

  readonly REACT_APP_ENABLE_VIRTUAL_MC?: 'true' | 'false';

  readonly REACT_APP_REINVENT2025_WS_CHALLENGES?: string;

  readonly REACT_APP_REINVENT2025_VIRTUALMC_VIDEO_SOURCES_URL?: string;

  /**
   * Enables PAR (Post-Action Report) feature for event reports
   */
  readonly REACT_APP_ENABLE_PAR_REPORT?: 'true' | 'false';

  /**
   * Enables AI Assistant chatbot feature
   */
  readonly REACT_APP_ENABLE_AI_ASSISTANT?: 'true' | 'false';

  /**
   * Enables async WebSocket-based challenge recommendations
   */
  readonly REACT_APP_ENABLE_ASYNC_CHALLENGE_RECOMMENDATIONS?: 'true' | 'false';

  /**
   * Enables variable (partial) scoring for Lambda-validated challenge tasks
   */
  readonly REACT_APP_ENABLE_VARIABLE_SCORING?: 'true' | 'false';

  /**
   * Enables global notifications
   */
  readonly REACT_APP_GLOBAL_NOTIFICATIONS_ENABLED?: 'true' | 'false';

  /**
   * Enables facilitator tools in the game UI facilitator view
   */
  readonly REACT_APP_ENABLE_FACILITATOR_TOOLS?: 'true' | 'false';

  /**
   * Enables AI challenge validation feature
   */
  readonly REACT_APP_ENABLE_CHALLENGE_VALIDATION?: 'true' | 'false';

  /**
   * Enabled AI teammate
   */
  readonly REACT_APP_ENABLE_AI_TEAMMATE?: 'true' | 'false';

  /**
   * Enabled user guide hosted on s3 docs bucket
   */
  readonly REACT_APP_ENABLE_S3_HOSTED_USER_GUIDE?: 'true' | 'false';

  /**
   * Enables replacing an active event challenge with a configured backup challenge
   */
  readonly REACT_APP_ENABLE_REPLACE_CHALLENGE_WITH_BACKUP?: 'true' | 'false';
  /**
   * Enable AI-judged Arena for SCENARIO challenges
   */
  readonly REACT_APP_ENABLE_ARENA?: 'true' | 'false';

  /**
   * Enables AWS service detection for challenge AWS settings
   */
  readonly REACT_APP_ENABLE_AWS_SERVICE_DETECTION?: 'true' | 'false';
}

export type EnvVarName = keyof Env;

/**
 * Get an environment variable as a string.
 *
 * @param variableName
 * @param defaultValue
 */
export const getEnvVar = (variableName: EnvVarName, defaultValue?: string): string => {
  const variable = process.env[variableName];
  if (!variable) {
    if (defaultValue != null) {
      return defaultValue;
    }
    throw new Error(`${variableName} environment variable must be defined`);
  }
  return variable;
};

/**
 * Get an environment variable as an integer.
 *
 * @param variableName
 * @param defaultValue
 */
export const getEnvVarAsInt = (variableName: EnvVarName, defaultValue?: number): number => {
  const envVarStr: string = getEnvVar(variableName, defaultValue != null ? defaultValue.toString() : undefined);
  return parseInt(envVarStr, 10);
};

/**
 * Set/delete an environment variable.
 *
 * @param variableName
 * @param value - The value for the environment variable.
 * If undefined, the environment variable is deleted.
 *
 */
export const setEnvVar = (variableName: EnvVarName, value: string | undefined): void => {
  if (value == null) {
    delete process.env[variableName];
  } else {
    process.env[variableName] = value;
  }
};
